import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  IconButton,
  Tooltip,
  List,
  ListItem,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Link,
  Center,
  Select,
  FormControl,
  FormLabel,
  useToast,
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  ChevronRightIcon,
  ChevronDownIcon,
  RepeatIcon,
  CloseIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import { FaFolder, FaFile, FaFileUpload } from 'react-icons/fa';
import GitLabService, { GitLabFile } from '../../services/gitlabService';
import { useTheme } from '../../hooks/useTheme';
import { useAppInfoContext } from '../../contexts/AppInfoContext';

interface GitLabFileBrowserProps {
  gitlabUrl?: string;
  onFileSelect?: (file: GitLabFile) => void;
  onFileUpload?: (file: GitLabFile) => void;
  onClose?: () => void;
  configGitlabUrl?: string; // Add config-specific URL for comparison
  onRepositoryChange?: (newUrl: string) => void; // Callback for repository changes
}

export const GitLabFileBrowser: React.FC<GitLabFileBrowserProps> = ({ 
  gitlabUrl, 
  onFileSelect,
  onFileUpload,
  onClose,
  configGitlabUrl,
  onRepositoryChange
}) => {
  const [files, setFiles] = useState<GitLabFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [error, setError] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<GitLabFile | null>(null);
  const [fileContent, setFileContent] = useState<any>(null);
  const [loadingFileInfo, setLoadingFileInfo] = useState(false);
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();
  const { appInfo } = useAppInfoContext();
  
  // Repository selection state
  const [availableRepos, setAvailableRepos] = useState<{label: string, value: string}[]>([]);
  const [userRepositories, setUserRepositories] = useState<Array<{id: number, name: string, url: string, description?: string}>>([]);
  const [selectedRepoUrl, setSelectedRepoUrl] = useState<string>('');
  const [customRepoUrl, setCustomRepoUrl] = useState<string>('');
  const [repoSelectMode, setRepoSelectMode] = useState<'configured' | 'accessible' | 'custom'>('configured');
  const [showRepoSelector, setShowRepoSelector] = useState(false);
  const [defaultRepoUrl, setDefaultRepoUrl] = useState<string | null>(null);
  const [loadingRepositories, setLoadingRepositories] = useState(false);
  
  const toast = useToast();

  const { themeConfig } = useTheme();
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (gitlabUrl) {
      loadFiles();
    }
  }, [gitlabUrl, currentPath]);

  // Load available repositories when component mounts
  useEffect(() => {
    loadAvailableRepos();
  }, [configGitlabUrl]);

  const loadAvailableRepos = async () => {
    try {
      // Get default repository
      const defaultRepo = await GitLabService.getDefaultRepository();
      setDefaultRepoUrl(defaultRepo.url);
      
      const repos: {label: string, value: string}[] = [];
      
      // Add current URL (resolved)
      if (gitlabUrl) {
        repos.push({
          label: `Current Repository: ${gitlabUrl.replace(/^https?:\/\//, '')}`,
          value: gitlabUrl
        });
      }
      
      // Add config-specific URL if different from current
      if (configGitlabUrl && configGitlabUrl !== gitlabUrl) {
        repos.push({
          label: `Document Repository: ${configGitlabUrl.replace(/^https?:\/\//, '')}`,
          value: configGitlabUrl
        });
      }
      
      // Add default repository if it exists and is different
      if (defaultRepo.url && defaultRepo.url !== gitlabUrl && defaultRepo.url !== configGitlabUrl) {
        repos.push({
          label: `Default Repository: ${defaultRepo.url.replace(/^https?:\/\//, '')}`,
          value: defaultRepo.url
        });
      }
      
      setAvailableRepos(repos);
      setSelectedRepoUrl(gitlabUrl || '');
    } catch (error) {
      console.error('Error loading available repositories:', error);
    }
  };

  const loadUserRepositories = async () => {
    setLoadingRepositories(true);
    try {
      // First check if GitLab token is configured
      const tokenCheck = await GitLabService.checkToken();
      if (!tokenCheck.hasToken) {
        toast({
          title: 'GitLab Token Required',
          description: 'Please configure your GitLab Personal Access Token in Settings → GitLab Integration first.',
          status: 'warning',
          duration: 7000,
          isClosable: true,
        });
        return;
      }

      const result = await GitLabService.getUserRepositories();
      if (result.success && result.repositories) {
        setUserRepositories(result.repositories);
      } else {
        console.error('Repository loading failed:', result.error);
        
        // Show specific error message based on the error
        let title = 'Failed to Load Repositories';
        let description = result.error || 'Could not fetch your accessible repositories';
        
        if (result.error?.includes('Authentication failed')) {
          title = 'GitLab Authentication Failed';
          description = 'Your GitLab token may be invalid or expired. Please update it in Settings → GitLab Integration.';
        } else if (result.error?.includes('Access forbidden')) {
          title = 'GitLab Access Denied';
          description = 'Your GitLab token may not have sufficient permissions. Please check token scopes.';
        }
        
        toast({
          title,
          description,
          status: 'error',
          duration: 7000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error loading user repositories:', error);
      toast({
        title: 'Repository Access Error',
        description: 'Failed to fetch your accessible repositories. Please check your GitLab configuration.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingRepositories(false);
    }
  };

  const handleRepoSelect = () => {
    let urlToUse = '';
    
    if (repoSelectMode === 'custom') {
      urlToUse = customRepoUrl.trim();
    } else {
      urlToUse = selectedRepoUrl;
    }
    
    if (!urlToUse) {
      toast({
        title: 'Repository Required',
        description: 'Please select or enter a repository URL',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Notify parent component about repository change
    if (onRepositoryChange) {
      onRepositoryChange(urlToUse);
      setCurrentPath(''); // Reset path when switching repos
      setShowRepoSelector(false); // Close the selector
    } else {
      toast({
        title: 'Repository Switch Not Supported',
        description: 'Repository switching is not available in this context',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const loadFiles = async () => {
    if (!gitlabUrl) return;
    
    setLoading(true);
    setError('');
    
    try {
      const result = await GitLabService.getFiles(gitlabUrl, currentPath, searchTerm);
      setFiles(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadFiles();
  };

  const handleFileClick = (file: GitLabFile) => {
    if (file.type === 'tree') {
      // Navigate to folder
      setCurrentPath(file.path);
    } else {
      // Select file
      onFileSelect?.(file);
    }
  };

  const handleUploadClick = async (file: GitLabFile) => {
    setSelectedFile(file);
    setLoadingFileInfo(true);
    onOpen();
    
    try {
      // You can add file metadata fetching here if needed
      // For now, we'll just show the file info we already have
      setFileContent(null);
    } catch (err) {
      console.error('Error loading file info:', err);
    } finally {
      setLoadingFileInfo(false);
    }
  };

  const confirmUpload = () => {
    if (selectedFile && onFileUpload) {
      onFileUpload(selectedFile);
      onModalClose();
      setSelectedFile(null);
      
      // Automatically close the Browse Repository sidebar after upload
      if (onClose) {
        onClose();
      }
    }
  };

  const handleModalClose = () => {
    onModalClose();
    setSelectedFile(null);
    setFileContent(null);
  };

  const goBack = () => {
    const pathParts = currentPath.split('/');
    pathParts.pop();
    setCurrentPath(pathParts.join('/'));
  };

  const refreshFiles = () => {
    loadFiles();
  };

  // Show message if no GitLab URL is provided
  if (!gitlabUrl) {
    return (
      <Card bg={cardBg} borderColor={borderColor} h="100%">
        <CardHeader pb={2}>
          <HStack justify="space-between" align="center">
            <Heading size="sm" color={textColor} isTruncated>
              Browse Repository
            </Heading>
            <HStack spacing={1} flexShrink={0}>
              <IconButton
                aria-label="Choose repository"
                icon={<SettingsIcon />}
                size="sm"
                onClick={() => {
                  setShowRepoSelector(!showRepoSelector);
                  if (!showRepoSelector && repoSelectMode === 'accessible' && userRepositories.length === 0) {
                    loadUserRepositories();
                  }
                }}
                variant="ghost"
                colorScheme={showRepoSelector ? themeConfig.colorScheme : undefined}
              />
              {onClose && (
                <IconButton
                  aria-label="Close repository browser"
                  icon={<CloseIcon />}
                  size="sm"
                  onClick={onClose}
                  variant="ghost"
                />
              )}
            </HStack>
          </HStack>
        </CardHeader>
        <CardBody p={4} h="calc(100% - 60px)" display="flex" flexDirection="column">
          <Center h="full">
            <VStack spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <VStack spacing={2} align="start">
                  <Text fontWeight="semibold">GitLab URL Not Configured</Text>
                  <Text fontSize="sm">Please configure a GitLab URL in Settings to browse repositories.</Text>
                </VStack>
              </Alert>
            </VStack>
          </Center>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg={cardBg} borderColor={borderColor} h="100%">
      <CardHeader pb={2}>
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between" align="center">
            <Heading size="sm" color={textColor} isTruncated>
              Browse Repository
            </Heading>
            <HStack spacing={1} flexShrink={0}>
              <IconButton
                aria-label="Choose repository"
                icon={<SettingsIcon />}
                size="sm"
                onClick={() => {
                  console.log('Settings clicked');
                  setShowRepoSelector(!showRepoSelector);
                  if (!showRepoSelector && repoSelectMode === 'accessible' && userRepositories.length === 0) {
                    loadUserRepositories();
                  }
                }}
                variant="ghost"
                colorScheme={showRepoSelector ? 'blue' : undefined}
              />
              <IconButton
                aria-label="Refresh files"
                icon={<RepeatIcon />}
                size="sm"
                onClick={() => {
                  console.log('Refresh clicked');
                  refreshFiles();
                }}
                isLoading={loading}
                variant="ghost"
              />
              {onClose && (
                <IconButton
                  aria-label="Close repository browser"
                  icon={<CloseIcon />}
                  size="sm"
                  onClick={() => {
                    console.log('Close clicked');
                    onClose();
                  }}
                  variant="ghost"
                />
              )}
            </HStack>
          </HStack>
          {gitlabUrl && (
            <Text fontSize="xs" color={mutedColor} fontFamily="mono" isTruncated>
              {gitlabUrl.replace(/^https?:\/\//, '')}
            </Text>
          )}
        </VStack>
      </CardHeader>
      
      <CardBody p={4} h="calc(100% - 60px)" display="flex" flexDirection="column">
        <VStack spacing={4} align="stretch" h="100%">
          {/* Repository Selector */}
          {showRepoSelector && (
            <Box bg={useColorModeValue('gray.50', 'gray.700')} p={3} borderRadius="md">
              <VStack spacing={3} align="stretch">
                <HStack spacing={2}>
                  <Button
                    size="xs"
                    variant={repoSelectMode === 'configured' ? 'solid' : 'ghost'}
                    onClick={() => setRepoSelectMode('configured')}
                  >
                    Configured
                  </Button>
                  <Button
                    size="xs"
                    variant={repoSelectMode === 'accessible' ? 'solid' : 'ghost'}
                    onClick={() => {
                      setRepoSelectMode('accessible');
                      if (userRepositories.length === 0) {
                        loadUserRepositories();
                      }
                    }}
                  >
                    My Repos
                  </Button>
                  <Button
                    size="xs"
                    variant={repoSelectMode === 'custom' ? 'solid' : 'ghost'}
                    onClick={() => setRepoSelectMode('custom')}
                  >
                    Custom URL
                  </Button>
                </HStack>

                {repoSelectMode === 'configured' && (
                  availableRepos.length > 0 ? (
                    <Select 
                      value={selectedRepoUrl} 
                      onChange={(e) => setSelectedRepoUrl(e.target.value)}
                      placeholder="Select a configured repository"
                      size="sm"
                    >
                      {availableRepos.map((repo, index) => (
                        <option key={index} value={repo.value}>
                          {repo.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Text fontSize="sm" color={mutedColor}>
                      No configured repositories. Configure a default repository in Settings.
                    </Text>
                  )
                )}

                {repoSelectMode === 'accessible' && (
                  loadingRepositories ? (
                    <HStack justify="center" py={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm">Loading your repositories...</Text>
                    </HStack>
                  ) : userRepositories.length > 0 ? (
                    <Select 
                      value={selectedRepoUrl} 
                      onChange={(e) => setSelectedRepoUrl(e.target.value)}
                      placeholder="Select from your accessible repositories"
                      size="sm"
                    >
                      {userRepositories.map((repo) => (
                        <option key={repo.id} value={repo.url}>
                          {repo.name} {repo.description && `- ${repo.description}`}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Text fontSize="sm" color={mutedColor}>
                      No accessible repositories found or GitLab token not configured.
                    </Text>
                  )
                )}

                {repoSelectMode === 'custom' && (
                  <Input
                    value={customRepoUrl}
                    onChange={(e) => setCustomRepoUrl(e.target.value)}
                    placeholder="https://gitlab.com/user/repo"
                    size="sm"
                  />
                )}

                <HStack justify="flex-end" spacing={2}>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => setShowRepoSelector(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    colorScheme={themeConfig.colorScheme} 
                    onClick={handleRepoSelect}
                    isDisabled={
                      (repoSelectMode === 'configured' && !selectedRepoUrl) ||
                      (repoSelectMode === 'accessible' && !selectedRepoUrl) ||
                      (repoSelectMode === 'custom' && !customRepoUrl.trim())
                    }
                  >
                    Switch Repository
                  </Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {/* Search Bar */}
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color={mutedColor} />
            </InputLeftElement>
            <Input
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </InputGroup>

          {/* Navigation */}
          <HStack spacing={2}>
            {currentPath && (
              <Button size="sm" variant="ghost" onClick={goBack}>
                ← Back
              </Button>
            )}
            <Text fontSize="sm" color={mutedColor} isTruncated>
              {currentPath || 'Root'}
            </Text>
          </HStack>

          <Divider />

          {/* Error Message */}
          {error && (
            <Alert status="error" size="sm">
              <AlertIcon />
              <Text fontSize="sm">{error}</Text>
            </Alert>
          )}

          {/* Files List */}
          <Box flex={1} overflowY="auto" minH={0}>
            {loading ? (
              <HStack justify="center" py={4}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={mutedColor}>Loading files...</Text>
              </HStack>
            ) : files.length === 0 ? (
              <Text fontSize="sm" color={mutedColor} textAlign="center" py={4}>
                No files found
              </Text>
            ) : (
              <List spacing={1}>
                {files.map((file) => (
                  <ListItem key={file.id}>
                    <HStack
                      p={2}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: hoverBg }}
                      onClick={() => handleFileClick(file)}
                      spacing={2}
                    >
                      {file.type === 'tree' ? (
                        <FaFolder color={`var(--chakra-colors-${themeConfig.colorScheme}-500)`} />
                      ) : (
                        <FaFile color={mutedColor} />
                      )}
                      
                      <Text 
                        fontSize="sm" 
                        color={textColor}
                        isTruncated
                        flex={1}
                      >
                        {file.name}
                      </Text>
                      
                      {file.type === 'blob' && onFileUpload && (
                        <Tooltip label="Upload file">
                          <IconButton
                            aria-label="Upload file"
                            icon={<FaFileUpload />}
                            size="xs"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUploadClick(file);
                            }}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </VStack>
      </CardBody>

      {/* Upload Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={handleModalClose} size="xl">
        <ModalOverlay />
        <ModalContent width="60vw" minH="45vh">
          <ModalHeader>Upload to {appInfo.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedFile && (
              <VStack align="start" spacing={3}>
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={mutedColor}>
                    Filename:
                  </Text>
                  <Text>{selectedFile.name}</Text>
                </Box>
                
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={mutedColor}>
                    Path:
                  </Text>
                  <Link 
                    href={selectedFile.rawUrl} 
                    target="_blank" 
                    color="blue.500"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {selectedFile.path}
                  </Link>
                </Box>
                
                <Box>
                  <Text fontWeight="bold" fontSize="sm" color={mutedColor}>
                    Type:
                  </Text>
                  <Badge variant="outline">
                    {selectedFile.name.split('.').pop()?.toUpperCase() || 'FILE'}
                  </Badge>
                </Box>

                <Alert status="info" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">
                    This file will be retrieved from GitLab and uploaded to the external API for processing.
                  </Text>
                </Alert>
              </VStack>
            )}
            
            {loadingFileInfo && (
              <HStack justify="center" py={4}>
                <Spinner size="sm" />
                <Text fontSize="sm">Loading file information...</Text>
              </HStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={confirmUpload}
              isDisabled={!selectedFile || loadingFileInfo}
            >
              Upload File
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Card>
  );
};